import { Component, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { helper } from 'src/environments/helper';
import { DocumenteCetateanService } from '../documente-cetatean.service';
import { NotificationService } from 'src/app/services/notification.service';
import { AuthService } from 'src/app/services/auth.service';
import { DocumenteCetateanAddPayload } from '../documente-cetatean.interface';
import { Observable, debounceTime, finalize, map, of, startWith, switchMap } from 'rxjs';
import { Generic } from 'src/app/interfaces/generic.interface';
import { CommonService } from 'src/app/services/common.service';
import { TipuriDocumenteService } from '../../../settings-sidenav/tipuri-documente/tipuri-documente.service';
import { notDefaultValidator } from 'src/app/shared/validators/not-default';
import { ToastrService } from 'ngx-toastr';
import { environment } from 'src/environments/environment';
import { StorageService } from 'src/app/utils/storage/storage.service';
import { ChangeDetectorRef } from '@angular/core';
import { MatSelect } from '@angular/material/select';

@Component({
    selector: 'app-add-document-cetatean',
    templateUrl: './add-document-cetatean.component.html',
    styleUrls: ['./add-document-cetatean.component.scss']
})
export class AddDocumentCetateanComponent implements OnInit {
    @ViewChild('directieSelect') directieSelect!: MatSelect;

    // general error
    errorTitle: string = helper.dialogConfig.headers.errorAtentie;
    errorIcon: string = helper.dialogConfig.icons.error;
    errorType: string = helper.dialogConfig.icons.error;

    successTitle: string = helper.dialogConfig.headers.success;
    successIcon: string = helper.dialogConfig.icons.success;
    successType: string = helper.dialogConfig.icons.success;

    helper = helper;

    token!: string;
    isLoading = false;
    isCnpLoading = false;

    dropdownControl = new FormControl();
    cnps: any[] = [];
    showMessage = true;
    loadComplete = false;

    directii!: Generic[];
    filteredDirectii!: Observable<Generic[]>;
    filterControl = new FormControl('');
    tipuriDocumente!: Generic[];
    compartimenteActive$!: Observable<Generic[]>;
    user!: any;

    defaultParameter: { name: string; id: string } = {
        name: '-- Alegeți --',
        id: '-1',
    };

    notificareParameters: { key: string; value: boolean }[] = [
        { key: 'Nu', value: false },
        { key: 'Da', value: true },
    ];

    uploading: any = {
        file: false,
    };

    files: any = {
        file: [],
    };

    form: FormGroup = new FormGroup({
        cnp_cif: new FormControl('', [Validators.required]),
        directie_id: new FormControl('-1', [Validators.required, notDefaultValidator("-1")]),
        directie_name: new FormControl('', [Validators.required]),
        compartiment_id: new FormControl('-1'),
        compartiment_name: new FormControl(''),
        document_type: new FormControl('-1', [Validators.required, notDefaultValidator("-1")]),
        document_type_name: new FormControl('', [Validators.required]),
        notificare_sms_allow: new FormControl(false),
        notificare_sms_number: new FormControl(''),
        notificare_email_allow: new FormControl(true),
        notificare_email_address: new FormControl('', [Validators.required]),
        files: this.fb.array([], [Validators.required])
    });

    constructor(
        private dialogRef: MatDialogRef<AddDocumentCetateanComponent>,
        private documenteCetateanService: DocumenteCetateanService,
        private notificationService: NotificationService,
        private authService: AuthService,
        private commonService: CommonService,
        private tipuriDocumenteService: TipuriDocumenteService,
        public toastr: ToastrService,
        private storageService: StorageService,
        private cdr: ChangeDetectorRef,
        private fb: FormBuilder
    ) {
        this.processToken();
        this.dropdownControl.valueChanges
            .pipe(debounceTime(600))
            .subscribe(cnp => {
                this.fetchCnps(cnp);
                this.showMessage = true;
                this.loadComplete = false;
            });
    }

    ngOnInit(): void {
        Promise.all([this.loadDirectiiActive()])
            .then(() => {
                this.getUserDetails();
                this.loadTipuriDocumente();
                this.filteredDirectii = this.filterControl.valueChanges.pipe(
                    startWith(''),
                    map((value: any) => this.filterDirectii(value))
                );
            })
            .catch(error => {
                console.error('Error loading data', error);
            });
    }

    filterDirectii(value: string): Generic[] {
        const filterValue = value.toLowerCase();
        return this.directii.filter(option => option.name.toLowerCase().includes(filterValue));
    }

    ngAfterViewInit() {
        this.directieSelect.openedChange.subscribe((opened) => {
            if (opened) {
                setTimeout(() => {
                    const panel = this.directieSelect.panel.nativeElement;
                    panel.scrollTo({ top: 0, behavior: 'smooth' });
                });
            }
        });
    }

    fetchCnps(cnp: string) {
        if (cnp.length >= 2) {
            this.isCnpLoading = true;
            this.documenteCetateanService.getCnp(cnp).subscribe({
                next: (response: any) => {
                    this.cnps = response.data;
                    this.isCnpLoading = false;
                    this.loadComplete = true;
                    this.showMessage = this.cnps.length === 0;
                },
                error: (err: any) => {
                    this.isCnpLoading = false;
                    this.showMessage = true;
                    this.loadComplete = true;
                    console.log(err);
                }
            });
        } else {
            this.cnps = [];
            this.showMessage = true;
            this.loadComplete = false;
        }
    }

    closeDialog() {
        this.dialogRef?.close({
            success: false,
        });
    }

    processToken() {
        const tokenObj = this.authService.returnTokenUser();
        tokenObj.then((token) => {
            this.token = token;
            this.documenteCetateanService.setToken(token);
        });
    }

    loadDirectiiActive(): Promise<void> {
        this.isLoading = true;
        return this.commonService
            .getDirectiiActive()
            .pipe(
                map((results: Generic[]) => [this.defaultParameter, ...results]),
                finalize(() => (this.isLoading = false))
            )
            .toPromise()
            .then((results: any) => {
                this.directii = results;
            })
            .catch((error) => {
                console.error('Error loading directii', error);
                this.isLoading = false;
            });
    }

    onDirectieChange(directieId: number): void {
        const selectedDirectie = this.directii.find(directie => directie.id === directieId);

        if (directieId) {
            this.loadCompartimenteActive(directieId);
            this.form.get('directie_name')?.patchValue(selectedDirectie?.name);
        } else {
            this.compartimenteActive$ = new Observable<Generic[]>((observer) => {
                observer.complete();
            });
        }
    }

    loadCompartimenteActive(directieId?: number) {
        this.isLoading = true;
        let compartimenteObservable$ = directieId
            ? this.commonService.getCompartimenteByDirectieId(directieId)
            : this.commonService.getCompartimenteActive();

        this.compartimenteActive$ = compartimenteObservable$.pipe(
            map((results: any) => [...results]),
            finalize(() => {
                this.isLoading = false;
            })
        );
    }

    onCompartimenteChange(compartimentId: number) {
        this.compartimenteActive$.pipe(
            switchMap(compartimente => of(compartimente)),
            map(compartimente => compartimente.find(compartiment => compartiment.id === compartimentId))
        ).subscribe(selectedCompartiment => {
            if (selectedCompartiment) {
                this.form.get('compartiment_name')?.patchValue(selectedCompartiment.name);
            }
        });
    }

    loadTipuriDocumente() {
        this.isLoading = true;

        this.tipuriDocumenteService
            .getTipuriDocumenteActive()
            .pipe(
                map((results: any) => [this.defaultParameter, ...results]),
                finalize(() => (this.isLoading = false))
            )
            .subscribe({
                next: (results) => {
                    this.tipuriDocumente = results;
                },
                error: (error) => {
                    console.error('Error loading tipuri documente', error);
                    this.isLoading = false;
                },
            });
    }

    onTipDocumentSelected(id: number) {
        const selectedTip = this.tipuriDocumente.find(tip => tip.id === id);

        if (selectedTip) {
            this.form.get('document_type_name')?.patchValue(selectedTip.name);
        }
    }

    addFile(event: any, key: 'file') {
        event.eventPreventDefault = true;
        const inputElement = event.target as HTMLInputElement;
        if (!inputElement.files) {
            return;
        }

        const files = inputElement.files;

        // Iterate through the selected files and append them to the 'files' array in the form
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            this.uploading[key] = true;

            const formData = new FormData();
            formData.append('file', file, file.name);

            this.documenteCetateanService.fileUpload(formData).subscribe({
                next: (result: any) => {
                    let response = typeof result.status_code !== 'undefined' ? result : result.error;
                    if (typeof response.status_code !== 'undefined') {
                        if (
                            response.status_code == 200 &&
                            typeof response.data !== 'undefined'
                        ) {
                            const files = this.form.get(
                                'files'
                            ) as FormArray;
                            files.push(new FormControl(result.data));

                            this.uploading[key] = false;
                            this.openToast({
                                title: this.successTitle,
                                message:
                                    helper.dialogConfig.generalMessages.fileSuccessfullyUploaded,
                                type: this.successIcon,
                            });
                        }
                    } else {
                        // Handle error cases here
                        let errorMessage =
                            environment.config.customNotifications.generalMessages.error;
                        this.notificationService.warningSwal(
                            this.errorTitle,
                            errorMessage,
                            this.errorIcon
                        );
                        this.isLoading = false;
                        this.uploading[key] = false;
                    }
                },
                error: (result: any) => {
                    this.isLoading = false;
                    this.uploading[key] = false;
                    this.onHttpError(result.error);
                },
            });
        }
    }

    removeFile(index: number) {
        const files = this.form.get('files') as FormArray;
        if (index > -1 && index < files.length) {
            files.removeAt(index);
        }
    }

    openToast(object: any) {
        const { type, message, title } = object;

        let options = this.toastr.toastrConfig;
        options.progressBar = true;
        options.preventDuplicates = true;
        options.closeButton = true;

        const inserted = this.toastr.show(
            message,
            title,
            options,
            options.iconClasses[type]
        );
    }

    onHttpError(res: any, showErrorsInToast: boolean = false) {
        if (res.errors && res.errors.message && res.errors.message.length > 0) {
            res.errors.message.forEach((msg: string) => {
                if (showErrorsInToast) {
                    this.openToast({
                        title: this.errorTitle,
                        message: msg,
                        type: this.errorType,
                    });
                } else {
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                }
            });
        } else {
            let errorMessage =
                environment.config.customNotifications.generalMessages.error;
            this.notificationService.warningSwal(
                this.errorTitle,
                errorMessage,
                this.errorIcon
            );
        }
    }

    getUserDetails() {
        this.isLoading = true;
        const userObj = this.storageService.getObject(environment.config.userKey);
        userObj
            .then((res) => {
                if (res.id_sesizare_departament !== null) {
                    this.form.get('directie_id')?.patchValue(res.id_sesizare_departament);
                    this.onDirectieChange(res.id_sesizare_departament);
                } else {
                    this.form.get('directie_id')?.patchValue('-1');
                }

                if (res.id_sesizare_compartiment !== null) {
                    this.form.get('compartiment_id')?.patchValue(res.id_sesizare_compartiment);
                    this.onCompartimenteChange(res.id_sesizare_compartiment);
                } else {
                    this.form.get('compartiment_id')?.patchValue('-1');
                }
            })
            .catch((err) => {
                console.error(err);
            });
    }

    onCnpChange(selectedCnp: string) {
        const selectedCitizen = this.cnps.find(citizen => citizen.cnp === selectedCnp)
        if (selectedCnp) {
            this.form.get('notificare_sms_number')?.patchValue(selectedCitizen.international_number);
            this.form.get('notificare_email_address')?.patchValue(selectedCitizen.email);
        }
    }

    onSmsChange(val: boolean) {
        let sms = this.form.get('notificare_sms_number') as FormControl;

        if (val === true) {
            sms.setValidators([Validators.required]);
        } else {
            sms.clearValidators();
            sms.setErrors(null);
            sms.markAsPristine();
            sms.markAsUntouched();
        }

        this.form.updateValueAndValidity();
        this.cdr.detectChanges();
    }

    onEmailChange(val: boolean) {
        let email = this.form.get('notificare_email_address') as FormControl;

        if (val === true) {
            email.setValidators([Validators.required]);
        } else {
            email.clearValidators();
            email.setErrors(null);
            email.markAsPristine();
            email.markAsUntouched();
        }

        this.form.updateValueAndValidity();
        this.cdr.detectChanges();
    }

    submitForm() {
        this.isLoading = true;

        const {
            cnp_cif,
            directie_id,
            directie_name,
            compartiment_id,
            compartiment_name,
            document_type,
            document_type_name,
            notificare_sms_allow,
            notificare_sms_number,
            notificare_email_allow,
            notificare_email_address,
            files
        } = this.form.value as DocumenteCetateanAddPayload;

        const cnp_cifNumeric = cnp_cif.replace(/\D/g, '');

        const newDocument: DocumenteCetateanAddPayload = {
            cnp_cif: cnp_cifNumeric,
            directie_id,
            directie_name,
            compartiment_id,
            compartiment_name,
            document_type,
            document_type_name,
            notificare_sms_allow,
            notificare_sms_number,
            notificare_email_allow,
            notificare_email_address,
            files
        };

        this.documenteCetateanService
            .addDocumentCetatean(newDocument)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (res) => {
                    this.dialogRef.close({ success: true });
                },
                error: (err) => {
                    let msg;
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }
}
